// LIVE-WORKSPACE: the DE's chat, docked beside the Workspace. The person reads and types in their
// DM with the DE they talk to on Live (the same DE the voice panel uses), next to the canvas,
// without leaving the page. Nothing here draws messages or a composer: the DM is Chat's own
// `ChannelScreen` (message list, composer, threads, cards), shown `embedded`. The DM is opened
// (made on first use, a closed one reopened) with `dms.open`, as Chat's Team list does. The dock
// collapses to a slim bar; the choice is kept per browser.
import { Button, NativeSelect, NativeSelectOption } from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDownIcon, ChevronUpIcon, MessageSquareIcon } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import type { Api } from "../../api/client";
import { ChannelScreen } from "../chat/channel-screen";
import { chatKeys } from "../chat/chat-queries";
import type { CompanyAccess } from "../company";
import { useFeature } from "../use-feature";
import { useSeatedDes } from "./voice-panel";

const COLLAPSED_KEY = "companyos.live.chat-collapsed";

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(v: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    // Private mode or blocked storage: the dock still works, it just forgets.
  }
}

export function DockedChat({
  api,
  company,
  deId,
  onDeChange,
  fallback,
}: {
  api: Api;
  company: CompanyAccess;
  /** The DE picked on the page (shared with the voice panel); "" until one is picked. */
  deId: string;
  onDeChange: (id: string) => void;
  /** Without Chat (the feature off) or without a DE to talk to: the plain launcher. */
  fallback: ReactNode;
}) {
  const co = company.id;
  const chat = useFeature("chat");
  const { des, seated } = useSeatedDes(api, co);
  const de = seated.find((d) => d.membership_id === deId) ?? seated[0] ?? null;
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => writeCollapsed(collapsed), [collapsed]);
  const queryClient = useQueryClient();
  const dm = useQuery({
    queryKey: ["live-dock", co, "dm", de?.membership_id ?? ""],
    queryFn: async () => {
      const c = await api.client.dms.open({
        params: { co },
        body: { membership_ids: [de?.membership_id as string] },
      });
      void queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) });
      return c;
    },
    enabled: chat.enabled && de !== null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  if (!chat.enabled || (des.isSuccess && !de)) return <>{fallback}</>;

  return (
    <aside
      aria-label="Chat"
      data-slot="workspace-chat"
      data-docked="true"
      data-collapsed={collapsed ? "true" : "false"}
      className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <MessageSquareIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        <h2 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold">
          {de ? `Chat with ${de.display_name}` : "Chat"}
        </h2>
        {seated.length > 1 && !collapsed ? (
          <NativeSelect
            size="sm"
            aria-label="Chat with"
            value={de?.membership_id ?? ""}
            onChange={(e) => onDeChange(e.target.value)}
          >
            {seated.map((d) => (
              <NativeSelectOption key={d.membership_id} value={d.membership_id}>
                {d.display_name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : null}
        <Button
          size="icon-sm"
          variant="ghost"
          aria-expanded={!collapsed}
          aria-label={collapsed ? "Show chat" : "Hide chat"}
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? (
            <ChevronDownIcon aria-hidden="true" />
          ) : (
            <ChevronUpIcon aria-hidden="true" />
          )}
        </Button>
      </div>
      {collapsed ? null : dm.data ? (
        <div className="flex h-[min(70vh,44rem)] min-h-0 flex-col">
          <ChannelScreen channelId={dm.data.id} embedded />
        </div>
      ) : dm.isError ? (
        <p role="alert" className="p-4 text-sm text-muted-foreground">
          The chat with {de?.display_name ?? "your DE"} could not be opened.
        </p>
      ) : (
        <p role="status" aria-label="Loading chat" className="p-4 text-sm text-muted-foreground">
          Opening the chat…
        </p>
      )}
    </aside>
  );
}
